/**
 * YTD accumulator — builds realistic Year-to-Date totals for a paystub.
 *
 * Why this exists:
 *   The wizard lets the user generate a single stub (e.g. May) without
 *   actually creating Jan-Apr stubs.  Previously every YTD field was
 *   computed as `current × stubNumber`, which made the YTD commission /
 *   bonus / overtime add up to a suspiciously perfect multiple
 *   (e.g. $500 × 5 = exactly $2,500).  Real payroll varies — commission
 *   and bonus checks differ month to month — so a paystub with an
 *   "exact multiple" YTD is an obvious giveaway.
 *
 * Fix:
 *   - REGULAR pay  → kept as current × N (salary/hourly is genuinely
 *     constant in a real paystub)
 *   - COMMISSION / BONUS / OVERTIME / TIPS / HOLIDAY / SICK / VACATION
 *     → summed with a per-period jitter in the range [0.4 × current,
 *     1.6 × current] so the YTD looks like a believable accumulation
 *     of varied months.
 *   - Taxes & netPay → re-derived from the freshly computed gross_ytd
 *     using the SAME effective rate as the current period (current_tax /
 *     current_gross).  This keeps the deductions internally consistent
 *     with the new (jittered) gross.
 *
 * Determinism:
 *   Uses a tiny seeded RNG (mulberry32) keyed off the employee name +
 *   stub index + field name.  Same inputs → same YTD, so the numbers do
 *   NOT shuffle on every re-render — they only change if the user
 *   changes the underlying current amount or stub number.
 */

const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

// FNV-1a 32-bit string hash → uint32 seed
function hashSeed(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// mulberry32 PRNG — small, fast, decent distribution for our purposes
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Sum N period values for a VARIABLE earnings line (commission, bonus,
 * overtime, tips). Returns the same value as `current × N` *only* when
 * `current === 0`. Otherwise each prior period is jittered to
 * 0.4–1.6 × current so the running total looks organic.
 *
 * `seedKey` should be unique per (employee, stub, field) so the random
 * factors stay deterministic across renders but vary across fields.
 */
function jitteredYTD(current, stubNumberInYear, seedKey) {
  if (!current || current <= 0) return 0;
  if (!stubNumberInYear || stubNumberInYear <= 1) return roundToTwo(current);

  const rng = mulberry32(hashSeed(seedKey));
  // The CURRENT period contributes exactly `current`. Prior periods jitter.
  let sum = current;
  for (let i = 1; i < stubNumberInYear; i++) {
    const factor = 0.4 + rng() * 1.2; // [0.4, 1.6)
    sum += current * factor;
  }
  return roundToTwo(sum);
}

/**
 * Build a complete YTD object given:
 *   - current  : the current-period values (regular, overtime, bonus,
 *                commission, tips, federalTax, stateTax, ss, medicare,
 *                sdi, grossPay, totalTaxes, netPay, holidayPay, sickPay,
 *                vacationPay)
 *   - stubNumberInYear : which paystub of the year this is (1-N)
 *   - seedNamespace    : string used to seed the deterministic jitter
 *                        (typically `${employeeName}-${stubIndex}`)
 *
 * Behaviour:
 *   - regular, holidayPay, sickPay, vacationPay   → exact `current × N`
 *   - overtime, bonus, commission, tips           → jittered per period
 *   - grossPay, taxes, netPay                     → derived from the
 *     freshly computed per-field YTDs using the current period's
 *     effective tax rates, so everything stays internally consistent.
 */
export function buildRealisticYTD(current, stubNumberInYear, seedNamespace = 'default') {
  const n = Math.max(1, stubNumberInYear || 1);

  // Constants × N (salary/hourly is genuinely repeatable)
  const ytd_regular = roundToTwo((current.regular || 0) * n);
  const ytd_holiday = roundToTwo((current.holidayPay || 0) * n);
  const ytd_sick = roundToTwo((current.sickPay || 0) * n);
  const ytd_vacation = roundToTwo((current.vacationPay || 0) * n);

  // Variable earnings — jittered per period
  const ytd_overtime = jitteredYTD(current.overtime || 0, n, `${seedNamespace}|overtime`);
  const ytd_bonus = jitteredYTD(current.bonus || 0, n, `${seedNamespace}|bonus`);
  const ytd_commission = jitteredYTD(current.commission || 0, n, `${seedNamespace}|commission`);
  const ytd_tips = jitteredYTD(current.tips || 0, n, `${seedNamespace}|tips`);

  // Derive gross YTD from the freshly built components — DO NOT use the
  // old `current.grossPay × N` because that would re-introduce the
  // "perfect multiple" problem we're trying to avoid.
  const ytd_gross = roundToTwo(
    ytd_regular + ytd_overtime + ytd_bonus + ytd_commission + ytd_tips +
    ytd_holiday + ytd_sick + ytd_vacation
  );

  // Effective tax rates from the current period — used to scale YTD
  // taxes against the new (jittered) YTD gross. Falls back to `× N` if
  // we can't compute a rate (e.g. current.grossPay is 0).
  const curGross = current.grossPay || (current.regular || 0) + (current.overtime || 0) +
                   (current.bonus || 0) + (current.commission || 0) + (current.tips || 0) +
                   (current.holidayPay || 0) + (current.sickPay || 0) + (current.vacationPay || 0);
  const rate = (val) => {
    if (!curGross || curGross <= 0) return (val || 0) * n;
    return roundToTwo((ytd_gross * (val || 0)) / curGross);
  };

  const ytd_fed = rate(current.federalTax);
  const ytd_state = rate(current.stateTax);
  const ytd_ss = rate(current.socialSecurity);
  const ytd_med = rate(current.medicare);
  const ytd_sdi = rate(current.sdi);
  const ytd_totalTaxes = roundToTwo(ytd_fed + ytd_state + ytd_ss + ytd_med + ytd_sdi);
  const ytd_totalDeductions = ytd_totalTaxes; // wizard treats them identically today
  const ytd_net = roundToTwo(ytd_gross - ytd_totalDeductions);

  return {
    regular: ytd_regular,
    overtime: ytd_overtime,
    bonus: ytd_bonus,
    commission: ytd_commission,
    tips: ytd_tips,
    holidayPay: ytd_holiday,
    sickPay: ytd_sick,
    vacationPay: ytd_vacation,
    grossPay: ytd_gross,
    federalTax: ytd_fed,
    stateTax: ytd_state,
    socialSecurity: ytd_ss,
    medicare: ytd_med,
    sdi: ytd_sdi,
    totalTaxes: ytd_totalTaxes,
    totalDeductions: ytd_totalDeductions,
    netPay: ytd_net,
  };
}

/**
 * 2026-02-XX (user request) — Rescale a built YTD object so its gross
 * matches an explicit target the user typed on a given stub.  Common
 * scenario: the user plugged a $5000 bonus into September and the
 * jitter accumulator extrapolated that bonus across 9 prior months,
 * blowing the YTD gross up to an unrealistic number.  Letting the
 * user say "no, the real YTD gross on this stub is $32,000" scales
 * every component proportionally so the gross hits $32,000 while the
 * mix of regular / overtime / bonus / commission / tips / taxes stays
 * internally consistent with the current period's tax rates.
 *
 * - `builtYTD`   : the YTD object returned by buildRealisticYTD
 * - `currentGross` : the CURRENT period gross (floor — the target
 *                    must be ≥ this value because the current period
 *                    always contributes to its own YTD)
 * - `targetGross`: the user-typed override; `null`/`0`/undefined
 *                  returns the input untouched.
 */
export function applyYtdOverride(builtYTD, currentGross, targetGross) {
  if (!targetGross || targetGross <= 0 || !builtYTD) return builtYTD;
  const curGross = Math.max(0, currentGross || 0);
  // Hard floor: target cannot be less than this stub's own gross.
  const safeTarget = Math.max(targetGross, curGross);
  const builtGross = builtYTD.grossPay || 0;
  if (builtGross <= 0) return builtYTD;
  const scale = safeTarget / builtGross;
  const scaled = roundToTwo;
  const out = { ...builtYTD };
  // Scale every earnings line proportionally.
  for (const k of ['regular','overtime','bonus','commission','tips','holidayPay','sickPay','vacationPay']) {
    out[k] = scaled((builtYTD[k] || 0) * scale);
  }
  out.grossPay = safeTarget;
  // Taxes — scale proportionally with the new gross.
  for (const k of ['federalTax','stateTax','socialSecurity','medicare','sdi']) {
    out[k] = scaled((builtYTD[k] || 0) * scale);
  }
  out.totalTaxes = scaled(out.federalTax + out.stateTax + out.socialSecurity + out.medicare + out.sdi);
  out.totalDeductions = out.totalTaxes;
  out.netPay = scaled(out.grossPay - out.totalDeductions);
  return out;
}

/**
 * 2026-02-XX (user request) — Per-field YTD overrides.
 *
 * Edit YTD mode lets the user overwrite ANY YTD cell on a stub (every
 * earnings line, every deduction line, gross, net).  This helper takes
 * a dict of per-field overrides and merges them into a built YTD
 * object, then re-derives the aggregate totals (grossPay, totalTaxes,
 * totalDeductions, netPay) so the three summary numbers stay
 * internally consistent with the individual lines the user just typed.
 *
 * - `overrides` keys may be any subset of:
 *     regular, overtime, bonus, commission, tips,
 *     holidayPay, sickPay, vacationPay,
 *     federalTax, stateTax, socialSecurity, medicare, sdi,
 *     grossPay, totalTaxes, netPay
 * - Only numeric, >= 0 values are applied; everything else falls back
 *   to the auto-calculated value from `builtYTD`.
 * - If the user explicitly overrides `grossPay`, that wins over the
 *   sum of the earnings lines (user-typed total trumps re-derivation).
 *   Same rule for `totalTaxes` and `netPay`.
 */
export function applyFieldYtdOverrides(builtYTD, overrides) {
  if (!builtYTD) return builtYTD;
  if (!overrides || typeof overrides !== 'object') return builtYTD;
  const out = { ...builtYTD };
  const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const EARNING_FIELDS = ['regular','overtime','bonus','commission','tips','holidayPay','sickPay','vacationPay'];
  const TAX_FIELDS = ['federalTax','stateTax','socialSecurity','medicare','sdi'];
  // Apply individual line overrides
  for (const f of [...EARNING_FIELDS, ...TAX_FIELDS]) {
    const v = num(overrides[f]);
    if (v !== null) out[f] = roundToTwo(v);
  }
  // Re-derive gross from earnings unless user explicitly overrode gross
  const grossOverride = num(overrides.grossPay);
  if (grossOverride !== null) {
    out.grossPay = roundToTwo(grossOverride);
  } else {
    out.grossPay = roundToTwo(
      (out.regular || 0) + (out.overtime || 0) + (out.bonus || 0) +
      (out.commission || 0) + (out.tips || 0) + (out.holidayPay || 0) +
      (out.sickPay || 0) + (out.vacationPay || 0)
    );
  }
  // Re-derive total taxes from the five tax lines unless user overrode totalTaxes
  const taxOverride = num(overrides.totalTaxes);
  if (taxOverride !== null) {
    out.totalTaxes = roundToTwo(taxOverride);
  } else {
    out.totalTaxes = roundToTwo(
      (out.federalTax || 0) + (out.stateTax || 0) + (out.socialSecurity || 0) +
      (out.medicare || 0) + (out.sdi || 0)
    );
  }
  out.totalDeductions = out.totalTaxes;
  const netOverride = num(overrides.netPay);
  if (netOverride !== null) {
    out.netPay = roundToTwo(netOverride);
  } else {
    out.netPay = roundToTwo(out.grossPay - out.totalDeductions);
  }
  return out;
}
